import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { User } from 'firebase/auth';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  Subscription,
  switchMap,
} from 'rxjs';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import {
  DashboardService,
  TopLineStatsResponseDto,
} from '@/app/pages/dashboard/service/dashboard/dashboard';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

@Component({
  selector: 'dbv2-top-stats-row',
  imports: [RouterLink],
  templateUrl: './top-stats-row.html',
  styleUrl: './top-stats-row.scss',
})
export class TopStatsRow implements OnDestroy {
  topLineStats = signal<TopLineStatsResponseDto | null>(null);
  lastVisit = signal<Date | null>(null);
  loadingState = signal<LoadingState>('idle');

  private readonly subscriptionList = new Subscription();

  private readonly currentUserService = inject(CurrentUserService);
  private readonly dashboardService = inject(DashboardService);
  private readonly firebaseService = inject(FirebaseService);

  constructor() {
    this.subscriptionList.add(
      combineLatest([
        this.firebaseService.user$.pipe(filter((user): user is User => !!user)),
        this.currentUserService.shoutUser$.pipe(filter((user): user is { latest_user_visit?: Date | string } => !!user)),
        this.currentUserService.focussedBrand$.pipe(
          filter((brand): brand is { id: string } => !!brand?.id),
          distinctUntilChanged((a, b) => a.id === b.id),
        ),
      ])
        .pipe(
          debounceTime(350),
          switchMap(([firebaseUser, shoutUser, brand]) => {
            this.loadingState.set('loading');
            const visit = this.visitDate(shoutUser.latest_user_visit);
            this.lastVisit.set(visit);
            // Force-refresh so the request carries the claims written by sync-claims.
            // A cached token from before that sync is what the API rejects with 403.
            return from(firebaseUser.getIdToken(true)).pipe(
              switchMap(() => this.dashboardService.getTopLineStats(brand.id, visit)),
              catchError((err: HttpErrorResponse) => {
                console.warn('top-line-stats failed', err.status, brand.id);
                this.topLineStats.set(null);
                this.loadingState.set('error');
                return EMPTY;
              }),
            );
          }),
        )
        .subscribe((stats) => {
          this.topLineStats.set(stats);
          this.loadingState.set('loaded');
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  private visitDate(raw: Date | string | null | undefined): Date | null {
    if (!raw) {
      return null;
    }
    const date = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
